import { EFFECTS, effectByKey, type ConditionGroup, type AutomationBranch } from '@capture/shared';

export const CHECK_EFFECTS = EFFECTS.filter((e) => e.bucket === 'check');
export const ROUTE_EFFECTS = EFFECTS.filter((e) => e.bucket === 'route');

export function bucketForEffect(effectKey: string): 'check' | 'fill_transform' | 'route' | null {
  return effectByKey(effectKey)?.bucket ?? null;
}

/** Field-scoped checks need a target field. cross_field_check + route do not. */
const FIELD_SCOPED_CHECKS = new Set(['validate_check', 'unique_check', 'historical_range_check', 'webhook_check']);
export function isFieldScopedCheck(effectKey: string): boolean {
  return FIELD_SCOPED_CHECKS.has(effectKey);
}

/** cross_field_check, row_check + every route effect are no-ops / always-fire
 *  without a condition (design §5; row_check's condition is the per-row invariant). */
export function ruleEffectRequiresCondition(effectKey: string): boolean {
  return effectKey === 'cross_field_check' || effectKey === 'row_check' || bucketForEffect(effectKey) === 'route';
}

/** Same rule as `ruleEffectRequiresCondition`, but keyed off an `AutomationBranch`
 *  instead of a raw effect key — route branches carry `actions[]`, not a single
 *  `effectKey`, so callers that only have a branch (the stepper's step-skip
 *  logic, the panel) should use this rather than re-deriving the family check
 *  inline. */
export function branchRequiresCondition(b: AutomationBranch): boolean {
  return b.family === 'route' || ruleEffectRequiresCondition(b.effectKey);
}

export function emptyConditionGroup(): ConditionGroup {
  return { logic: 'and', conditions: [] };
}
export function isConditionEmpty(c: ConditionGroup | undefined): boolean {
  return !c || c.conditions.length === 0;
}

/**
 * Sensible new-rule config seed per effect. Server-data checks MUST carry
 * `validationType` or `checkRuleCompleteness` blocks publish (uniqueness/
 * historicalRange/webhook alike). validate_check's validationType is the
 * friendly field-engine vocab (required/regex/…), which rides in actions[0].config.
 */
export function defaultConfigForEffect(effectKey: string): Record<string, unknown> {
  switch (effectKey) {
    case 'unique_check': return { validationType: 'uniqueness', scope: 'batch' };
    case 'historical_range_check': return { validationType: 'historicalRange', stdDeviations: 3 };
    case 'webhook_check': return { validationType: 'webhook', url: '', timeoutMs: 10000, failMode: 'soft' };
    case 'validate_check': return { validationType: 'required' };
    case 'row_check': return { rowGroup: '' };
    case 'route_to_queue': return { targetStep: '' };
    case 'notify': return { targetRole: 'operator' };
    case 'set_field': return { targetField: '', value: '', valueKind: 'literal', mode: 'replace' };
    case 'reprioritize': return { priority: 50 };
    default: return {}; // cross_field_check, hold, reject, escalate, skip_step, reclassify
  }
}
